import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { styles } from './safety-reports-page.style.js';
import { SafetyReportType, SafetyReportStatus } from '@cmms/models';
import { getSafetyReportsByCompany, getLocationsByCompany } from '@cmms/api';
import { db } from '../../services/auth.service.js';

@customElement('safety-reports-page')
export class SafetyReportsPage extends LitElement {
  static styles = styles;

  @property({ type: String }) companyId: string = '';
  
  @state() private safetyReports: any[] = [];
  @state() private locations: any[] = [];
  @state() private loading = false;
  @state() private error = '';
  @state() private filters = {
    status: '',
    type: '',
    search: ''
  };

  connectedCallback() {
    super.connectedCallback();
    this.loadSafetyReports();
  }

  private async loadSafetyReports() {
    if (!this.companyId) return;
    
    this.loading = true;
    this.error = '';

    try {
      const [reportsResponse, locationsResponse] = await Promise.all([
        getSafetyReportsByCompany(db, this.companyId),
        getLocationsByCompany(db, this.companyId)
      ]);
      
      this.safetyReports = reportsResponse.data ? 
        reportsResponse.data.map(item => ({
          id: item.id,
          ...(item.model as any).toJSON()
        })) : [];
      
      this.locations = locationsResponse.data ? 
        locationsResponse.data.map(item => ({
          id: item.id,
          ...(item.model as any).toJSON()
        })) : [];
        
    } catch (err) {
      this.error = 'Failed to load safety reports';
      console.error('Error loading safety reports:', err);
    } finally {
      this.loading = false;
    }
  }

  private getFilteredReports() {
    return this.safetyReports.filter(report => {
      const matchesStatus = !this.filters.status || report.status === this.filters.status;
      const matchesType = !this.filters.type || report.report_type === this.filters.type;
      const matchesSearch = !this.filters.search || 
        report.title.toLowerCase().includes(this.filters.search.toLowerCase()) ||
        report.description.toLowerCase().includes(this.filters.search.toLowerCase());
      
      return matchesStatus && matchesType && matchesSearch;
    });
  }

  private getStatusBadgeVariant(status: string) {
    switch (status) {
      case SafetyReportStatus.OPEN:
        return 'warning';
      case SafetyReportStatus.ASSIGNED:
        return 'info';
      case SafetyReportStatus.INVESTIGATING:
        return 'primary';
      case SafetyReportStatus.RESOLVED:
        return 'success';
      default:
        return 'neutral';
    }
  }

  private getTypeBadgeVariant(type: string) {
    switch (type) {
      case SafetyReportType.SAFETY_OBSERVATION:
        return 'warning';
      case SafetyReportType.NEAR_MISS:
        return 'info';
      case SafetyReportType.INCIDENT:
        return 'danger';
      default:
        return 'neutral';
    }
  }

  private formatDate(timestamp: number) {
    return new Date(timestamp).toLocaleDateString();
  }

  private getLocationName(locationId: string): string {
    if (!locationId) return '';
    const location = this.locations.find(loc => loc.id === locationId);
    return location ? location.name : '';
  }

  private getTableConfig() {
    return {
      columns: [
        {
          key: 'title',
          title: 'Title',
          sortable: true,
          render: (value: string, row: any) => html`
            <div style="font-weight: 500; color: var(--sl-color-neutral-700);">
              ${value}
            </div>
          `
        },
        {
          key: 'report_type',
          title: 'Type',
          sortable: true,
          render: (value: string) => html`
            <sl-badge variant="${this.getTypeBadgeVariant(value)}" class="type-badge">
              ${value.replace('_', ' ').toUpperCase()}
            </sl-badge>
          `
        },
        {
          key: 'status',
          title: 'Status',
          sortable: true,
          render: (value: string) => html`
            <sl-badge variant="${this.getStatusBadgeVariant(value)}" class="status-badge">
              ${value.toUpperCase()}
            </sl-badge>
          `
        },
        {
          key: 'location_id',
          title: 'Location',
          sortable: true,
          render: (value: string) => html`${this.getLocationName(value) || 'Unknown Location'}`
        },
        {
          key: 'date_reported',
          title: 'Date Reported',
          sortable: true,
          render: (value: number) => this.formatDate(value)
        },
        {
          key: 'actions',
          title: 'Actions',
          sortable: false,
          render: (value: any, row: any) => html`
            <div class="actions">
              <sl-button size="small" variant="text" @click="${() => this.viewReport(row)}">
                <sl-icon name="eye"></sl-icon>
              </sl-button>
              <sl-button size="small" variant="text" @click="${() => this.editReport(row)}">
                <sl-icon name="pencil"></sl-icon>
              </sl-button>
            </div>
          `
        }
      ]
    };
  }

  private viewReport(report: any) {
    const modal = document.createElement('safety-report-viewer') as any;
    modal.report = report;
    document.body.appendChild(modal);
  }

  private editReport(report: any) {
    const modal = document.createElement('safety-report-modal') as any;
    modal.report = report;
    modal.addEventListener('safety-report-saved', () => {
      this.loadSafetyReports();
    });
    document.body.appendChild(modal);
  }

  private createNewReport() {
    const modal = document.createElement('safety-report-modal') as any;
    modal.addEventListener('safety-report-saved', () => {
      this.loadSafetyReports();
    });
    document.body.appendChild(modal);
  }

  private onFilterChange(filterType: string, value: string) {
    this.filters = { ...this.filters, [filterType]: value };
  }

  render() {
    const filteredReports = this.getFilteredReports();

    return html`
      <div class="page-header">
        
        <sl-button variant="primary" @click="${this.createNewReport}">
          <sl-icon name="plus" slot="prefix"></sl-icon>
          New Report
        </sl-button>
      </div>

      <div class="filters">
        <div class="filter-group">
          <label class="filter-label">Search</label>
          <sl-input
            placeholder="Search reports..."
            value="${this.filters.search}"
            @input="${(e: any) => this.onFilterChange('search', e.target.value)}"
          >
            <sl-icon name="search" slot="prefix"></sl-icon>
          </sl-input>
        </div>

        <div class="filter-group">
          <label class="filter-label">Status</label>
          <sl-select
            placeholder="All Statuses"
            value="${this.filters.status}"
            @sl-change="${(e: any) => this.onFilterChange('status', e.target.value)}"
          >
            <sl-option value="">All Statuses</sl-option>
            <sl-option value="${SafetyReportStatus.OPEN}">Open</sl-option>
            <sl-option value="${SafetyReportStatus.ASSIGNED}">Assigned</sl-option>
            <sl-option value="${SafetyReportStatus.INVESTIGATING}">Investigating</sl-option>
            <sl-option value="${SafetyReportStatus.RESOLVED}">Resolved</sl-option>
          </sl-select>
        </div>

        <div class="filter-group">
          <label class="filter-label">Type</label>
          <sl-select
            placeholder="All Types"
            value="${this.filters.type}"
            @sl-change="${(e: any) => this.onFilterChange('type', e.target.value)}"
          >
            <sl-option value="">All Types</sl-option>
            <sl-option value="${SafetyReportType.SAFETY_OBSERVATION}">Safety Observation</sl-option>
            <sl-option value="${SafetyReportType.NEAR_MISS}">Near Miss</sl-option>
            <sl-option value="${SafetyReportType.INCIDENT}">Incident</sl-option>
          </sl-select>
        </div>
      </div>

      ${this.error ? html`
        <sl-alert variant="danger" open>
          <sl-icon name="exclamation-triangle" slot="icon"></sl-icon>
          ${this.error}
        </sl-alert>
      ` : ''}

      ${this.loading ? html`
        <div class="loading-container">
          <sl-spinner></sl-spinner>
        </div>
      ` : filteredReports.length === 0 ? html`
        <div class="empty-state">
          <sl-icon name="shield-exclamation"></sl-icon>
          <h3>No safety reports found</h3>
          <p>Create your first safety report to get started.</p>
        </div>
      ` : html`
        <data-table
          .data="${filteredReports}"
          .config="${this.getTableConfig() as any}"
        ></data-table>
      `}
    `;
  }
}
